import React, { useState, useEffect } from 'react';
import { FaChild, FaSchool, FaCalendarAlt, FaChartLine, FaFileAlt, FaShoppingCart, FaExclamationTriangle } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../common/LoadingSpinner';
import { formatDate, formatTime, isToday } from '../../utils/helpers';
import SubscriptionAlert from '../dashboard/SubscriptionAlert';

/**
 * Parent dashboard component
 * Displays an overview of children, upcoming activities, and recent events
 */
const ParentDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    children: [],
    upcomingEvents: [],
    recentActivity: [],
    stats: {
      totalChildren: 0,
      pendingTasks: 0,
      upcomingEvents: 0,
      savedSchools: 0,
    }
  });
  const [errors, setErrors] = useState({});
  const { showError } = useToast();

  // Fetch dashboard data on component mount
  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Fetch dashboard data from API
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setErrors({});
      
      const response = await api.get('/parents/dashboard');
      // Ensure we have valid data with proper structure
      const responseData = response.data.data || {};
      
      // Track missing data
      const missingData = {};
      
      // Set data and track missing sections
      const newData = {
        children: responseData.children || [],
        upcomingEvents: responseData.upcomingEvents || [],
        recentActivity: responseData.recentActivity || [],
        stats: {
          totalChildren: responseData.childrenCount || 0,
          pendingTasks: responseData.stats?.pendingTasks || 0,
          upcomingEvents: responseData.stats?.upcomingEvents || 0,
          savedSchools: responseData.stats?.savedSchools || 0,
        }
      };
      
      // Check for missing data
      if (!responseData.children) missingData.children = true;
      if (!responseData.upcomingEvents) missingData.events = true;
      if (!responseData.recentActivity) missingData.activity = true;
      if (!responseData.stats) missingData.stats = true;
      
      setDashboardData(newData);
      
      // Set errors if any data is missing
      if (Object.keys(missingData).length > 0) {
        setErrors({
          type: 'incomplete',
          missingData,
          message: 'Some dashboard data is unavailable.'
        });
      }
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      showError('Failed to load dashboard data');
      
      if (error.response) {
        setErrors({ 
          type: 'serverError', 
          message: 'Server error: ' + (error.response.data?.message || 'Unable to retrieve data.'),
          allMissing: true
        });
      } else if (error.request) {
        setErrors({ 
          type: 'connectionError', 
          message: 'Cannot connect to the server. Please check your network connection.',
          allMissing: true
        });
      } else {
        setErrors({ 
          type: 'unknownError', 
          message: 'An unexpected error occurred while loading data.',
          allMissing: true
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Format date with highlight for today
  const formatDateWithHighlight = (dateString) => {
    if (isToday(dateString)) {
      return <span className="text-primary font-medium">Today, {formatTime(dateString)}</span>;
    }
    return formatDate(dateString);
  };

  // Render full error state if no data is available
  if (!loading && errors.allMissing) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Parent Dashboard</h1>
            <p className="text-gray-600 mt-1">
              Welcome to your parent dashboard
            </p>
          </div>
        </div>
        
        <SubscriptionAlert />
        
        <div className="bg-white rounded-lg shadow-soft p-8 text-center mt-6">
          <FaExclamationTriangle className="text-yellow-500 text-5xl mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">
            {errors.type === 'connectionError' ? 'Connection Error' : 'Data Unavailable'}
          </h2>
          <p className="text-gray-600 mb-4">{errors.message}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn-primary inline-flex items-center"
          >
            <FaCalendarAlt className="mr-2" /> Retry Loading
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Parent Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Welcome to your parent dashboard
          </p>
        </div>
      </div>

      {/* Subscription Alert */}
      <SubscriptionAlert />
      
      {/* Global error message */}
      {errors.type && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <FaExclamationTriangle className="text-yellow-500" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">{errors.message}</p>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <>
          {/* Stats Overview */}
          <div className="mb-6">
            {errors.missingData?.stats && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4">
                <p className="text-sm text-yellow-700 flex items-center">
                  <FaExclamationTriangle className="text-yellow-500 mr-2" />
                  Dashboard statistics are unavailable
                </p>
              </div>
            )}
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Children */}
              <div className="bg-white rounded-lg shadow-soft p-6">
                <div className="flex items-center">
                  <div className="rounded-full bg-blue-100 p-3 mr-4">
                    <FaChild className="text-blue-500 text-xl" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Children</p>
                    <h3 className="text-2xl font-bold">{dashboardData.stats.totalChildren}</h3>
                  </div>
                </div>
                <div className="mt-4">
                  <Link
                    to="/parent/students"
                    className="text-primary text-sm font-medium hover:underline"
                  >
                    Manage children
                  </Link>
                </div>
              </div>

              {/* Pending Tasks */}
              <div className="bg-white rounded-lg shadow-soft p-6">
                <div className="flex items-center">
                  <div className="rounded-full bg-yellow-100 p-3 mr-4">
                    <FaFileAlt className="text-yellow-500 text-xl" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Pending Tasks</p>
                    <h3 className="text-2xl font-bold">{dashboardData.stats.pendingTasks}</h3>
                  </div>
                </div>
                <div className="mt-4">
                  <Link
                    to="/parent/progress"
                    className="text-primary text-sm font-medium hover:underline"
                  >
                    View tasks
                  </Link>
                </div>
              </div>

              {/* Upcoming Events */}
              <div className="bg-white rounded-lg shadow-soft p-6">
                <div className="flex items-center">
                  <div className="rounded-full bg-green-100 p-3 mr-4">
                    <FaCalendarAlt className="text-green-500 text-xl" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Upcoming Events</p>
                    <h3 className="text-2xl font-bold">{dashboardData.stats.upcomingEvents}</h3>
                  </div>
                </div>
                <div className="mt-4">
                  <Link
                    to="/events"
                    className="text-primary text-sm font-medium hover:underline"
                  >
                    View all events
                  </Link>
                </div>
              </div>

              {/* Saved Schools */}
              <div className="bg-white rounded-lg shadow-soft p-6">
                <div className="flex items-center">
                  <div className="rounded-full bg-purple-100 p-3 mr-4">
                    <FaSchool className="text-purple-500 text-xl" />
                  </div>
                  <div>
                    <p className="text-gray-500 text-sm">Saved Schools</p>
                    <h3 className="text-2xl font-bold">{dashboardData.stats.savedSchools}</h3>
                  </div>
                </div>
                <div className="mt-4">
                  <Link
                    to="/parent/schools"
                    className="text-primary text-sm font-medium hover:underline"
                  >
                    Search schools
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Children Summary */}
            <div className="lg:col-span-2 bg-white rounded-lg shadow-soft">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="font-semibold text-lg">My Children</h2>
                <Link
                  to="/parent/students"
                  className="text-primary text-sm font-medium hover:underline"
                >
                  Manage
                </Link>
              </div>
              
              {errors.missingData?.children && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 m-4">
                  <p className="text-sm text-yellow-700 flex items-center">
                    <FaExclamationTriangle className="text-yellow-500 mr-2" />
                    Children data is unavailable
                  </p>
                </div>
              )}
              
              <div className="p-6">
                {errors.missingData?.children ? (
                  <div className="text-center py-8">
                    <FaExclamationTriangle className="text-yellow-500 text-3xl mx-auto mb-2" />
                    <p className="text-gray-500">Children data unavailable</p>
                  </div>
                ) : dashboardData.children.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 mb-4">No children added yet</p>
                    <Link
                      to="/parent/students"
                      className="btn-primary inline-block"
                    >
                      Add Child
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {dashboardData.children.map((child) => (
                      <div
                        key={child._id}
                        className="p-4 border border-gray-100 rounded-lg hover:border-primary transition flex items-center"
                      >
                        {child.profilePicture ? (
                          <img
                            src={child.profilePicture}
                            alt={child.firstName}
                            className="w-12 h-12 rounded-full object-cover mr-4"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center mr-4">
                            <FaChild className="text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="font-semibold">
                            {child.firstName} {child.lastName}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {child.age} years • {child.grade || 'Grade not set'}
                          </p>
                        </div>
                        <div className="flex space-x-2">
                          <Link
                            to={`/parent/progress?childId=${child._id}`}
                            className="text-gray-500 hover:text-primary"
                            title="View Progress"
                          >
                            <FaChartLine />
                          </Link>
                          <Link
                            to={`/parent/students?edit=${child._id}`}
                            className="text-gray-500 hover:text-primary"
                            title="Edit Details"
                          >
                            <FaFileAlt />
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Upcoming Events */}
            <div className="bg-white rounded-lg shadow-soft">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="font-semibold text-lg">Upcoming Events</h2>
              </div>
              
              {errors.missingData?.events && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 m-4">
                  <p className="text-sm text-yellow-700 flex items-center">
                    <FaExclamationTriangle className="text-yellow-500 mr-2" />
                    Events data is unavailable
                  </p>
                </div>
              )}
              
              <div className="p-6">
                {errors.missingData?.events ? (
                  <div className="text-center py-8">
                    <FaExclamationTriangle className="text-yellow-500 text-3xl mx-auto mb-2" />
                    <p className="text-gray-500">Events data unavailable</p>
                  </div>
                ) : dashboardData.upcomingEvents.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No upcoming events</p>
                ) : (
                  <div className="space-y-4">
                    {dashboardData.upcomingEvents.map((event) => (
                      <div key={event._id} className="flex items-start">
                        <div className="rounded-full bg-blue-100 p-2 mr-3 mt-1">
                          <FaCalendarAlt className="text-blue-500 text-sm" />
                        </div>
                        <div>
                          <h3 className="font-medium">{event.title}</h3>
                          <p className="text-sm text-gray-500">{formatDateWithHighlight(event.date)}</p>
                          {event.child && (
                            <p className="text-xs text-gray-400 mt-1">For: {event.child}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="mt-6">
            <div className="bg-white rounded-lg shadow-soft">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="font-semibold text-lg">Recent Activity</h2>
              </div>
              
              {errors.missingData?.activity && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 m-4">
                  <p className="text-sm text-yellow-700 flex items-center">
                    <FaExclamationTriangle className="text-yellow-500 mr-2" />
                    Activity data is unavailable
                  </p>
                </div>
              )}
              
              <div className="p-6">
                {errors.missingData?.activity ? (
                  <div className="text-center py-8">
                    <FaExclamationTriangle className="text-yellow-500 text-3xl mx-auto mb-2" />
                    <p className="text-gray-500">Activity data unavailable</p>
                  </div>
                ) : dashboardData.recentActivity.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No recent activity</p>
                ) : (
                  <div className="space-y-4">
                    {dashboardData.recentActivity.map((activity) => (
                      <div key={activity._id} className="flex">
                        <div className="mr-4">
                          {activity.type === 'progress' && (
                            <div className="rounded-full bg-green-100 p-2">
                              <FaChartLine className="text-green-500" />
                            </div>
                          )}
                          {activity.type === 'task' && (
                            <div className="rounded-full bg-yellow-100 p-2">
                              <FaFileAlt className="text-yellow-500" />
                            </div>
                          )}
                          {activity.type === 'purchase' && (
                            <div className="rounded-full bg-purple-100 p-2">
                              <FaShoppingCart className="text-purple-500" />
                            </div>
                          )}
                          {activity.type === 'school' && (
                            <div className="rounded-full bg-blue-100 p-2">
                              <FaSchool className="text-blue-500" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
                            <p className="font-medium text-gray-900">{activity.message}</p>
                            <span className="text-sm text-gray-500">{formatDateWithHighlight(activity.date)}</span>
                          </div>
                          {activity.childName && (
                            <p className="text-sm text-gray-500">Child: {activity.childName}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ParentDashboard;
